import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyUnsubscribeToken, updatePreferencesWithToken } from '../../../data/reducers/emailPreferencesReducer';
import './EmailPreferences.css';

const EmailPreferences = () => {
  const { token } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { tokenValid, isVerifying, isUpdating, error, success } = useSelector(state => state.emailPreferences);

  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    if (token) {
      dispatch(verifyUnsubscribeToken(token));
    }
  }, [dispatch, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAction) return;

    try {
      const result = await dispatch(updatePreferencesWithToken(token, {
        action: selectedAction,
        ...(selectedAction === 'snooze' ? { snooze_days: 30 } : {}),
      }));

      if (result && selectedAction === 'unsubscribe') {
        history.push(`/unsubscribe-feedback/${token}`);
      }
    } catch (err) {
      console.error('Failed to update email preferences:', err);
    }
  };

  if (token && (isVerifying || tokenValid === null)) {
    return (
      <div className="email-preferences-scope email-preferences-page-container">

        <div className="email-preferences-main-card">
          <div style={{textAlign: 'center', padding: '40px'}}>
            <div className="loading-spinner"></div>
            Verifying your request...
          </div>
        </div>
      </div>
    );
  }

  if (tokenValid === false && !isVerifying) {
    return (
      <div className="email-preferences-scope email-preferences-page-container">

        <div className="email-preferences-main-card">
          <div style={{textAlign: 'center'}}>
            <div style={{fontSize: '60px', marginBottom: '20px'}}>😞</div>
            <h2 className="email-preferences-main-title">Oops, looks like this link is invalid</h2>
            <p className="email-preferences-subtitle">{error || 'This unsubscribe link has expired or is no longer valid.'}</p>
            <p style={{fontSize: '13px', color: '#999', marginBottom: '30px'}}>
              If you'd like to manage your email preferences, please sign in to your account.
            </p>
            <div className="email-preferences-actions">
              <button
                className="email-preferences-submit-btn"
                onClick={() => window.location.href = '/'}
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
        <div className="email-preferences-page-footer">
          2025 Violerts. All rights reserved.
        </div>
      </div>
    );
  }

  // Show the unsubscribed confirmation screen when:
  //   1. The user unsubscribed directly from this page (success string check), OR
  //   2. The user arrived here from UnsubscribeFeedback after submitting feedback
  //      (location.state.unsubscribed flag) — in this case verifyUnsubscribeToken
  //      fires on mount and would overwrite `success` before the string check runs.
  const arrivedFromFeedback = location.state?.unsubscribed === true;
  if (arrivedFromFeedback || (success && success.toLowerCase().includes('unsubscribed'))) {
    return (
      <div className="email-preferences-scope email-preferences-page-container">
        <div className="email-preferences-main-card">
          <div style={{textAlign: 'center'}}>
            <div style={{width: '60px', height: '60px', backgroundColor: '#28DF92', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', color: 'white', margin: '0 auto 30px'}}>
              ✓
            </div>
            <h2 className="email-preferences-main-title">You've Been Unsubscribed</h2>
            <p className="email-preferences-subtitle">
              You will no longer receive email notifications from Violerts.
            </p>
            <p style={{fontSize: '13px', color: '#999', marginBottom: '30px'}}>
              Your account remains active and you can still access all features by signing in.
            </p>

            <div className="email-preferences-actions">
              <button
                className="email-preferences-submit-btn"
                onClick={() => window.location.href = '/'}
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>

        <div className="email-preferences-page-footer">
          2025 Violerts. All rights reserved.
        </div>
      </div>
    );
  }

  // Success state - after successful snooze/resubscribe
  if (success && (success.toLowerCase().includes('snoozed') || success.toLowerCase().includes('resubscribed'))) {
    return (
      <div className="email-preferences-scope email-preferences-page-container">
        <div className="email-preferences-main-card">
          <div style={{textAlign: 'center'}}>
            <div style={{width: '60px', height: '60px', backgroundColor: '#28DF92', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', color: 'white', margin: '0 auto 30px'}}>
              ✓
            </div>
            <h2 className="email-preferences-main-title">Preferences Updated</h2>
            <p className="email-preferences-subtitle">
              {success}
            </p>

            <div className="email-preferences-actions">
              <button
                className="email-preferences-submit-btn"
                onClick={() => window.location.href = '/'}
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>

        <div className="email-preferences-page-footer">
          2025 Violerts. All rights reserved.
        </div>
      </div>
    );
  }

  return (
    <div className="email-preferences-scope email-preferences-page-container">


      <div className="email-preferences-main-card">
        <h2 className="email-preferences-main-title">Email Preferences</h2>
        <p className="email-preferences-subtitle">Customize how you hear from us for maximum inbox efficiency</p>

        {error && (
          <div style={{backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: '4px', padding: '10px', marginBottom: '20px', color: '#d32f2f', fontSize: '14px'}}>
            {error}
          </div>
        )}

        {success && (
          <div style={{backgroundColor: '#e8f5e8', border: '1px solid #4caf50', borderRadius: '4px', padding: '10px', marginBottom: '20px', color: '#2e7d32', fontSize: '14px'}}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="email-preferences-form-section">
            <h3 className="email-preferences-section-title">Need a break?</h3>
            <div className="email-preferences-option-wrapper">
              <label className="email-preferences-checkbox-label">
                <input
                  type="checkbox"
                  className="email-preferences-checkbox-input"
                  checked={selectedAction === 'snooze'}
                  onChange={(e) => setSelectedAction(e.target.checked ? 'snooze' : null)}
                  disabled={isUpdating}
                />
                <span className="email-preferences-custom-checkbox"></span>
                <span className="email-preferences-option-text">
                  Snooze Violerts emails for 30 days
                </span>
              </label>
            </div>
          </div>

          <div className="email-preferences-form-section">
            <h3 className="email-preferences-section-title">Would you rather unsubscribe?</h3>
            <div className="email-preferences-option-wrapper">
              <label className="email-preferences-checkbox-label">
                <input
                  type="checkbox"
                  className="email-preferences-checkbox-input"
                  checked={selectedAction === 'unsubscribe'}
                  onChange={(e) => setSelectedAction(e.target.checked ? 'unsubscribe' : null)}
                  disabled={isUpdating}
                />
                <span className="email-preferences-custom-checkbox"></span>
                <span className="email-preferences-option-text">
                  Unsubscribe from all emails
                </span>
              </label>
            </div>
          </div>

          <div className="email-preferences-actions">
            <button
              type="submit"
              className="email-preferences-submit-btn"
              disabled={!selectedAction || isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Preferences'}
            </button>
          </div>
        </form>
      </div>

      <div className="email-preferences-page-footer">
        2025 Violerts. All rights reserved.
      </div>
    </div>
  );
};

export default EmailPreferences;